import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Activity,
  ActivityAttendance,
  ActivityCreated,
  ActivityInput,
  ActivitySession,
  ActivitySessionDetail,
  ActivityType,
  AwardBadgeResult,
  ListActivitiesResponse,
  ListActivitySessionsResponse,
  SessionCreateInput,
  SessionUpdateInput,
} from 'shared';

export type { Activity, ActivityCreated, ActivityInput, ActivitySession, ActivitySessionDetail };

// ─── Query key factory ────────────────────────────────────────────────────────

export const activityKeys = {
  all: ['activities'] as const,
  list: (params: ListParams) => [...activityKeys.all, 'list', params] as const,
  detail: (id: number) => [...activityKeys.all, 'detail', id] as const,
  sessions: (id: number) => [...activityKeys.all, 'sessions', id] as const,
  session: (id: number) => [...activityKeys.all, 'session', id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ListParams {
  limit?: number;
  offset?: number;
  search?: string;
  type?: ActivityType | '';
}

/** How often an open session's roster is refreshed while it is on screen. */
export const ROSTER_REFRESH_MS = 5_000;

// ─── Activities ───────────────────────────────────────────────────────────────

/** Paginated, searchable list of activities, filterable by type. */
export function useActivities(params: ListParams = {}) {
  const { limit = 20, offset = 0, search = '', type = '' } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(search ? { search } : {}),
    ...(type ? { type } : {}),
  });

  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () => api.get<ListActivitiesResponse>(`/api/activities?${qs}`),
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  });
}

/** Single activity by ID. */
export function useActivity(id: number | null) {
  return useQuery({
    queryKey: activityKeys.detail(id!),
    queryFn: () => api.get<Activity>(`/api/activities/${id}`),
    enabled: id !== null,
  });
}

/**
 * Create a new activity, together with its session when the input has one.
 * The answer carries the sessions created, so the page can go straight there.
 */
export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ActivityInput) => api.post<ActivityCreated>('/api/activities', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

/** Update an existing activity. */
export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ActivityInput }) =>
      api.put<Activity>(`/api/activities/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

/** Archive an activity. Its sessions and attendance stay on record. */
export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/activities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

/** Every session of an activity, oldest first. */
export function useActivitySessions(activityId: number | null) {
  return useQuery({
    queryKey: activityKeys.sessions(activityId!),
    queryFn: () =>
      api.get<ListActivitySessionsResponse>(`/api/activities/${activityId}/sessions`),
    enabled: activityId !== null,
    staleTime: 1000 * 30,
  });
}

/** Create one session, or one per occurrence when the input repeats. */
export function useCreateSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ activityId, data }: { activityId: number; data: SessionCreateInput }) =>
      api.post<ListActivitySessionsResponse>(`/api/activities/${activityId}/sessions`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

/**
 * One session with its QR and roster. Refetched every few seconds so the
 * roster fills in as participants scan the code on the slide.
 */
export function useActivitySession(id: number | null) {
  return useQuery({
    queryKey: activityKeys.session(id!),
    queryFn: () => api.get<ActivitySessionDetail>(`/api/activity-sessions/${id}`),
    enabled: id !== null,
    refetchInterval: ROSTER_REFRESH_MS,
  });
}

/** Update a session: times, leader, visiting group and notes. */
export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SessionUpdateInput }) =>
      api.put<ActivitySession>(`/api/activity-sessions/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

/** Cancel a session. */
export function useCancelSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/activity-sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: activityKeys.all }),
  });
}

// ─── Roster ───────────────────────────────────────────────────────────────────

/** Add a participant to the roster by hand, or put back one who was removed. */
export function useAddAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, participantId }: { sessionId: number; participantId: number }) =>
      api.post<ActivityAttendance>(`/api/activity-sessions/${sessionId}/attendances`, {
        participantId,
      }),
    onSuccess: (_, { sessionId }) =>
      qc.invalidateQueries({ queryKey: activityKeys.session(sessionId) }),
  });
}

/** Take a participant off the roster. */
export function useRemoveAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, participantId }: { sessionId: number; participantId: number }) =>
      api.delete<void>(`/api/activity-sessions/${sessionId}/attendances/${participantId}`),
    onSuccess: (_, { sessionId }) =>
      qc.invalidateQueries({ queryKey: activityKeys.session(sessionId) }),
  });
}

/** Give the activity's badge to everyone present who does not hold it yet. */
export function useAwardBadge() {
  return useMutation({
    mutationFn: (sessionId: number) =>
      api.post<AwardBadgeResult>(`/api/activity-sessions/${sessionId}/award-badge`),
  });
}
